import unique from '@/filters/unique';
import DoceboAPI, {
  DoceboCatalogueView,
  DoceboCatalogueViewItem,
  DoceboCourseType,
  DoceboEnrolmentItemCourse,
  DoceboEnrolmentItemLearningPlan,
} from '@/lib/DoceboAPI';
import Vue from 'vue';
import Vuex, { ActionTree, GetterTree, MutationTree } from 'vuex';

interface LocalState {
  learningPlanEnrolments: {
    [enrolmentId: string]: DoceboEnrolmentItemLearningPlan;
  };
  courseEnrolments: { [enrolmentId: string]: DoceboEnrolmentItemCourse };
}

Vue.use(Vuex);

const doceboAPI = DoceboAPI.instance;

const namespaced = true;

const localState: LocalState = {
  learningPlanEnrolments: {},
  courseEnrolments: {},
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getters: GetterTree<LocalState, any> = {
  getEnrolmentStatusesByCatalogueId: (state, _getters, rootState) => (
    catalogueId: number
  ): {
    learningPlanEnrolments: {
      [enrolmentId: string]: DoceboEnrolmentItemLearningPlan;
    };
    courseEnrolments: {
      [enrolmentId: string]: DoceboEnrolmentItemCourse;
    };
  } => {
    return {
      courseEnrolments: Object.keys(state.courseEnrolments)
        // Filter the enrolments by getting a map of the catalogue IDs
        // and checking if the enrolment ID exists inside
        .filter((enrolmentId) =>
          rootState.catalogues.catalogueViews[catalogueId].data.items
            .map(
              (catalogueItem: DoceboCatalogueViewItem) => catalogueItem.item_id
            )
            .includes(enrolmentId)
        )
        // With the filtered enrolments, create a new
        // object containing these enrolments
        .reduce(
          (obj, id) => ({
            ...obj,
            [id]: state.courseEnrolments[id],
          }),
          {}
        ),
      learningPlanEnrolments: Object.keys(state.learningPlanEnrolments)
        // Filter the enrolments by getting a map of the catalogue IDs
        // and checking if the enrolment ID exists inside
        .filter((enrolmentId) =>
          rootState.catalogues.catalogueViews[catalogueId].data.items
            .map(
              (catalogueItem: DoceboCatalogueViewItem) => catalogueItem.item_id
            )
            .includes(enrolmentId)
        )
        // With the filtered enrolments, create a new
        // object containing these enrolments
        .reduce(
          (obj, id) => ({
            ...obj,
            [id]: state.learningPlanEnrolments[id],
          }),
          {}
        ),
    };
  },
};

const mutations: MutationTree<LocalState> = {
  SET_ENROLMENT_STATUS_ITEMS: (
    state,
    payload: (DoceboEnrolmentItemCourse | DoceboEnrolmentItemLearningPlan)[]
  ) => {
    payload.forEach((enrolment) => {
      switch (enrolment.type) {
        case DoceboCourseType.LEARNING_PLAN: {
          Vue.set(
            state.learningPlanEnrolments,
            enrolment.id.toString(),
            enrolment
          );
          break;
        }
        case DoceboCourseType.CLASSROOM:
        case DoceboCourseType.ELEARNING:
        case DoceboCourseType.WEBINAR:
        default: {
          Vue.set(state.courseEnrolments, enrolment.id.toString(), enrolment);
          break;
        }
      }
    });
  },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const actions: ActionTree<LocalState, any> = {
  GET_ENROLMENT_STATUS_FOR_ALL_CATALOGUES: (context): Promise<void> => {
    const courseIds: number[] = [];
    const learningPlanIds: number[] = [];
    Object.keys(context.rootState.catalogues.catalogueViews).forEach(
      (catalogueId) => {
        const catalogueView =
          context.rootState.catalogues.catalogueViews[catalogueId];
        catalogueView.data.items.forEach((item: DoceboCatalogueViewItem) => {
          if (item.course_type === DoceboCourseType.LEARNING_PLAN) {
            learningPlanIds.push(Number(item.item_id));
          } else {
            courseIds.push(Number(item.item_id));
          }
        });
      }
    );
    // Remove duplicate course IDs and learning plan IDs (some are shared across catalogues)
    const uniqueCourseIds = courseIds.filter(unique);
    const uniqueLearningPlanIds = learningPlanIds.filter(unique);
    return doceboAPI
      .getCourseEnrolments(uniqueCourseIds, uniqueLearningPlanIds)
      .then(
        (enrolments) => {
          context.commit('SET_ENROLMENT_STATUS_ITEMS', enrolments);
        },
        (error) => Promise.reject(error)
      );
  },

  GET_ENROLMENT_STATUS_FOR_CATALOGUE: (
    context,
    catalogueId: number
  ): Promise<void> => {
    const catalogueView: DoceboCatalogueView =
      context.rootState.catalogues.catalogueViews[catalogueId];
    const courseIds: number[] = [];
    const learningPlanIds: number[] = [];
    catalogueView.data.items.forEach((item) => {
      if (item.course_type === DoceboCourseType.LEARNING_PLAN) {
        learningPlanIds.push(Number(item.item_id));
      } else {
        courseIds.push(Number(item.item_id));
      }
    });
    return doceboAPI.getCourseEnrolments(courseIds, learningPlanIds).then(
      (enrolments) => {
        context.commit('SET_ENROLMENT_STATUS_ITEMS', enrolments);
      },
      (error) => Promise.reject(error)
    );
  },

  ENROL_COURSE: (context, courseId: string): Promise<void> =>
    doceboAPI
      .enrolOnCourse(courseId)
      .then(() => doceboAPI.getCourseEnrolments([Number(courseId)], []))
      .then(
        (enrolments) => {
          context.commit('SET_ENROLMENT_STATUS_ITEMS', enrolments);
        },
        (error) => Promise.reject(error)
      ),

  RESET_COURSE: (context, courseId: string): Promise<void> =>
    doceboAPI
      .resetCourse(courseId)
      .then(() => doceboAPI.getCourseEnrolments([Number(courseId)], []))
      .then(
        (enrolments) => {
          context.commit('SET_ENROLMENT_STATUS_ITEMS', enrolments);
        },
        (error) => Promise.reject(error)
      ),

  ENROL_COURSE_SESSION: (
    context,
    payload: {
      courseId: string;
      sessionId: string;
    }
  ): Promise<void> =>
    doceboAPI
      .enrolOnCourseSession(payload.courseId, payload.sessionId)
      .then(() => doceboAPI.getCourseEnrolments([Number(payload.courseId)], []))
      .then(
        (enrolments) => {
          context.commit('SET_ENROLMENT_STATUS_ITEMS', enrolments);
        },
        (error) => Promise.reject(error)
      )
      .then(() =>
        context.dispatch('sessions/GET_COURSE_SESSIONS', payload.courseId, {
          root: true,
        })
      ),

  ENROL_LEARNING_PLAN: (context, learningPlanId): Promise<void> =>
    doceboAPI
      .enrolOnLearningPlan(learningPlanId)
      .then(() => doceboAPI.getCourseEnrolments([], [Number(learningPlanId)]))
      .then(
        (enrolments) => {
          context.commit('SET_ENROLMENT_STATUS_ITEMS', enrolments);
        },
        (error) => Promise.reject(error)
      ),

  RESET_LEARNING_PLAN: (context, learningPlanId: string): Promise<void> =>
    doceboAPI
      .resetLearningPlan(learningPlanId)
      .then(() => doceboAPI.getCourseEnrolments([], [Number(learningPlanId)]))
      .then(
        (enrolments) => {
          context.commit('SET_ENROLMENT_STATUS_ITEMS', enrolments);
        },
        (error) => Promise.reject(error)
      ),
};

export { namespaced, localState as state, getters, mutations, actions };
